import { useEffect, useState } from 'react'
import { Modal, VStack, HStack } from '@musinsa/mwms-ds'
import { Icon } from '@musinsa/pbo-assets'
import { AxiosError } from 'axios'
import styled from 'styled-components'
import { Spinner } from '@/components/content/Loader'
import Text from '@/components/content/Text'
import Button from '@/components/content/Button'
import { colorSystem } from '@/styles/theme'
import MToast from '@/utils/modal/MToast'
import { Utils } from '@/utils/Utils'
import { PRICE_EXCEL_ERROR_CODES, PriceExcelErrorCode, uploadPriceExcel, validatePriceExcel } from '@/services/settlement/settlementPricing/api'

interface Props {
  initialFile: File
  refetch: () => void
  onClose: () => void
  onRetry: () => void
  onDownloadTemplate: () => void
}

// 'TEMPORARY' = 엑셀 코드가 아닌 일시적/알 수 없는 오류 (Figma node 7-7629)
type ErrorState = PriceExcelErrorCode | 'TEMPORARY'
type UploadState = ErrorState | 'UPLOADING' | 'SUCCESS'

// 실패 유형별 카드 문구 (Figma MFS 정산관리 7-7630/7631/7574/7601/7628/7629)
const ERROR_VIEW: Record<ErrorState, { title: string; message: string }> = {
  [PRICE_EXCEL_ERROR_CODES.EXCEL_ROW_COUNT_LIMIT_EXCEEDED]: {
    title: '등록 실패',
    message: '한 번에 최대 1,000건까지 업로드 가능해요.\n파일을 분할하거나 건수를 줄여 재업로드해 주세요.',
  },
  [PRICE_EXCEL_ERROR_CODES.EXCEL_TOTAL_VALIDATION_FAILURE]: {
    title: '파일 업로드 실패',
    message: '모든 행에 오류가 있어 업로드에 실패했어요.\n엑셀파일을 수정하여 재업로드 해주세요.',
  },
  [PRICE_EXCEL_ERROR_CODES.EXCEL_PARTIAL_VALIDATION_FAILURE]: {
    title: '파일 업로드 실패',
    message: '일부 행에 오류가 있어 업로드에 실패했어요.\n엑셀파일을 수정하여 재업로드 해주세요.',
  },
  [PRICE_EXCEL_ERROR_CODES.INVALID_EXCEL_FILE]: {
    title: '파일 업로드 실패',
    message: '업로드 양식에 맞지 않는 파일이에요.\n양식을 다운받아 맞춰 다시 업로드해 주세요.',
  },
  TEMPORARY: {
    title: '등록 실패',
    message: '일시적인 오류로 처리에 실패했어요.\n문제가 지속될 경우 개발팀에 문의해주세요.',
  },
}

// 엑셀 일괄 업로드 (multipart·all-or-nothing·최대 1,000건·FR-PM12)
export function PricingExcelUploadModal({ initialFile, refetch, onClose, onRetry, onDownloadTemplate }: Props) {
  const [state, setState] = useState<UploadState>('UPLOADING')
  const [failedRows, setFailedRows] = useState<string>()
  const [serverMessage, setServerMessage] = useState<string>()

  // API 오류 → 실패 유형 상태로 변환. 엑셀 코드가 아니면 TEMPORARY 카드로 가되,
  // BE 검증 메시지(INVALID_PARAMETER 등: 기간중복·기본단가 부재 등)가 있으면 그대로 노출한다. (LOGISPRD-9863)
  const applyError = (e: unknown) => {
    const response = (e as AxiosError<ApiResponseV2>).response?.data as ApiResponseV2 | undefined
    const errorCode = response?.meta.errorCode as PriceExcelErrorCode | undefined
    if (errorCode === PRICE_EXCEL_ERROR_CODES.EXCEL_PARTIAL_VALIDATION_FAILURE) {
      setFailedRows(response?.meta.message)
      setState(errorCode)
      return
    }
    if (errorCode && errorCode in ERROR_VIEW) {
      setState(errorCode)
      return
    }
    setServerMessage(response?.meta.message || undefined)
    setState('TEMPORARY')
  }

  const handleValidation = async (file: File) => {
    setState('UPLOADING')
    try {
      const response = await validatePriceExcel(file)
      if (response.meta.result === 'SUCCESS') setState('SUCCESS')
    } catch (e) {
      applyError(e)
    }
  }

  useEffect(() => {
    handleValidation(initialFile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRegister = async () => {
    try {
      setState('UPLOADING')
      const { data } = await uploadPriceExcel(initialFile)
      MToast.show(`${Utils.getCommaNum(data?.savedCount ?? 0)}건의 단가가 등록되었습니다`)
      await refetch()
      onClose()
    } catch (e) {
      applyError(e)
    }
  }

  const handleCopyFailedRows = () => {
    if (failedRows) {
      navigator.clipboard.writeText(failedRows)
      MToast.show('실패 행 번호를 복사했습니다.')
    }
  }

  if (state === 'UPLOADING') {
    return (
      <Modal open customWidth={440} onClose={onClose}>
        <Wrap>
          <LoadingBox>
            <Spinner />
            <Text kind="Body_14_regular">파일을 업로드 중이에요.</Text>
          </LoadingBox>
          <FooterRight>
            <Button size="md" style={{ minWidth: 74 }} kind="secondary" content="취소" onClick={onClose} />
            <Button size="md" style={{ minWidth: 74 }} kind="secondary" content="등록" disabled />
          </FooterRight>
        </Wrap>
      </Modal>
    )
  }

  if (state === 'SUCCESS') {
    return (
      <Modal open customWidth={440} onClose={onClose}>
        <Wrap>
          <TitleBox>
            <Text kind="Headline_20_bold" style={{ textAlign: 'center' }}>
              아래 파일로 단가를 일괄 등록할까요?
            </Text>
            <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
              등록 후 일괄 취소가 불가하니 파일을 한번 더 확인해 주세요.
            </Text>
          </TitleBox>
          <FileBox>
            <HStack gap={16} align="center" style={{ padding: 16 }}>
              <Text kind="Body_14_regular" style={{ flex: 1, minWidth: 0 }}>
                {initialFile.name}
              </Text>
              <Button fit size="sm" kind="secondary" content="변경" onClick={onRetry} />
            </HStack>
          </FileBox>
          <FooterRight>
            <Button size="md" style={{ minWidth: 74 }} kind="secondary" content="취소" onClick={onClose} />
            <Button size="md" style={{ minWidth: 74 }} kind="primary_light" content="등록" onClick={handleRegister} />
          </FooterRight>
        </Wrap>
      </Modal>
    )
  }

  // ── 실패 카드 (Figma 에러 케이스) ──
  const view = ERROR_VIEW[state]
  const isPartial = state === PRICE_EXCEL_ERROR_CODES.EXCEL_PARTIAL_VALIDATION_FAILURE
  const isInvalid = state === PRICE_EXCEL_ERROR_CODES.INVALID_EXCEL_FILE
  const isTemporary = state === 'TEMPORARY'

  return (
    <Modal open customWidth={440} onClose={onClose}>
      <ErrorWrap>
        <HStack gap={8} align="flex-start">
          <Icon name="ic-alertTriangle" size={22} color={colorSystem.text.common.error} />
          <VStack gap={8} style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
            <Text kind="Headline_16_bold">{view.title}</Text>
            <Text kind="Body_14_regular" color={colorSystem.text.common.secondary} style={{ whiteSpace: 'pre-line' }}>
              {(isTemporary && serverMessage) || view.message}
            </Text>
          </VStack>
        </HStack>

        {isPartial && (
          <FileBox>
            <VStack gap={8} style={{ padding: 16 }}>
              <Text kind="Body_12_regular" color={colorSystem.text.common.secondary}>
                실패 행
              </Text>
              <Text kind="Body_14_regular" style={{ maxHeight: 168, overflow: 'auto', wordBreak: 'break-word' }}>
                {failedRows}
              </Text>
              <HStack>
                <Button fit size="sm" kind="secondary" content="복사" onClick={handleCopyFailedRows} />
              </HStack>
            </VStack>
          </FileBox>
        )}

        {isInvalid && (
          <FileBox>
            <HStack gap={16} align="center" style={{ padding: 16 }}>
              <Text kind="Body_14_regular" style={{ flex: 1, minWidth: 0 }}>
                MFS 카테고리 단가 일괄 등록 양식
              </Text>
              <Button fit size="sm" kind="secondary" content="다운로드" onClick={onDownloadTemplate} />
            </HStack>
          </FileBox>
        )}

        <FooterRight>
          {isTemporary ? (
            <Button size="md" style={{ minWidth: 74 }} kind="primary_light" content="확인" onClick={onClose} />
          ) : (
            <>
              <Button size="md" style={{ minWidth: 74 }} kind="secondary" content="취소" onClick={onClose} />
              <Button size="md" style={{ minWidth: 100 }} kind="primary_light" content="재업로드" onClick={onRetry} />
            </>
          )}
        </FooterRight>
      </ErrorWrap>
    </Modal>
  )
}

const Wrap = styled(VStack)`
  margin: 8px 0;
  gap: 24px;
  align-items: center;
`

// 에러 카드: 아이콘+제목 좌측, 메시지 좌측, 버튼 우측 하단 (Figma)
const ErrorWrap = styled(VStack)`
  gap: 20px;
  align-items: stretch;
  width: 100%;
`

const TitleBox = styled(VStack)`
  gap: 8px;
  align-items: center;
  width: 416px;
`

const FooterRight = styled(HStack)`
  gap: 8px;
  justify-content: flex-end;
`

const LoadingBox = styled(VStack)`
  width: 100%;
  padding: 32px 0;
  gap: 16px;
  align-items: center;
  justify-content: center;
  background-color: ${colorSystem.bg.filter};
  border-radius: 8px;
`

const FileBox = styled.div`
  width: 100%;
  background-color: ${colorSystem.bg.filter};
  border-radius: 8px;
  overflow: hidden;
`
